import {useContext, useMemo} from 'react';
import useExperiment from '../../../../hooks/useExperiment';
import getOverrideParams from '../../../../utils/parseUrlParams';
import {
  EXPERIMENT_CONFIGS,
  EXPERIMENT_GIFT_ADVISOR_ON_OVERVIEW,
} from '../../../../constants/experiments';
import UserStateContext from '../../../UserStateContext';

const useGiftAdvisorOnOverviewExperiment = () => {
  const {userStateData, isFetching: isFetchingUserData = true} = useContext(
    UserStateContext,
  );

  const experimentName = EXPERIMENT_GIFT_ADVISOR_ON_OVERVIEW;
  const hasUserVisitedRegistry =
    !isFetchingUserData &&
    (userStateData?.hasVisitedRegistry ||
      typeof userStateData?.registryFirstVisitDate === 'string');

  const config = EXPERIMENT_CONFIGS[experimentName]?.[process.env.ENV || 'qa'];
  const {experimentId, variants} = config ?? {};

  const controlVariantId = variants?.control?.id;

  const override = getOverrideParams(experimentId);
  const hasOverride = typeof override === 'string';

  const shouldUseExperiment = !hasOverride && hasUserVisitedRegistry;

  const {
    isLoading,
    isActive,
    variant: hookVariant,
    isInControl: hookIsInControl,
  } = useExperiment({
    name: experimentName,
    isEnabled: shouldUseExperiment,
  });

  const resolvedVariant = hasOverride ? override : hookVariant;

  const isExperimentLoading = useMemo(() => {
    if (hasOverride) return false;
    if (!hasUserVisitedRegistry) return true;

    return isLoading;
  }, [hasOverride, hasUserVisitedRegistry, isLoading]);

  const isInControl = useMemo(() => {
    if (!controlVariantId) return false;
    if (!isActive) return true;

    if (hasOverride) {
      return (
        override?.toString().trim() === controlVariantId?.toString().trim()
      );
    }

    return !!hookIsInControl;
  }, [isActive, hasOverride, override, hookIsInControl, controlVariantId]);

  return useMemo(() => {
    return {
      experimentId,
      variant: resolvedVariant,
      isExperimentLoading,
      isInControl,
    };
  }, [resolvedVariant, isExperimentLoading, isInControl, experimentId]);
};

export default useGiftAdvisorOnOverviewExperiment;
